import { AxisMeasuringRange, BarGraph, BarGraphChannel } from 'icreate/common/models/graphs';

export async function printBarGraph(graph: BarGraph, echarts, oldGraph?: BarGraph) {
    const graphChannels = graph.channels;
    const oldGraphChannels = oldGraph ? (oldGraph.channels ?? []) : null;

    if (!graphChannels || graphChannels.length === 0) {
        return;
    }

    const echartsInstance = echarts.default || echarts;

    const isPercentage = graph.displayType == AxisMeasuringRange.Relative;
    const divisions = graph.divisions ?? 6;

    const channelsIdsSet: Set<string> = new Set();
    graphChannels.forEach((channel: BarGraphChannel) => {
        if (channel?.channel?.fcuData?.fcuChannelId) {
            channelsIdsSet.add(channel.channel.fcuData.fcuChannelId);
        }
    });

    const maxLimit = findMaxLimit(graphChannels);
    const minLimit = findMinLimit(graphChannels);
    const yAxisDecimalPlaces = findMaxDecimalPlaces(graphChannels);

    const { notBorderedLables, borderedLables, changedIndexes } = prepareXAxislabel(graphChannels, oldGraphChannels);

    const channelValues = generateChannelValues(graphChannels);
    const valueLabels = prepareValueLabels(channelValues);
    const graphValuesArray = prepareGraphValuesArray(channelValues, maxLimit, minLimit, isPercentage);

    let graphYAxisMax = isPercentage ? 100 : Number(maxLimit);
    let graphYAxisMin = isPercentage ? 0 : Number(minLimit);

    if (isNaN(graphYAxisMax)) graphYAxisMax = 100;
    if (isNaN(graphYAxisMin)) graphYAxisMin = 0;
    if (graphYAxisMax <= graphYAxisMin) {
        graphYAxisMax = graphYAxisMin + 1;
    }

    const titleBorder =
        oldGraph && oldGraph?.title != graph.title ? [true, true, true, true] : [false, false, false, false];

    const barGraphDivision = document.createElement('div');
    barGraphDivision.style.height = '490pt';
    barGraphDivision.style.width = '600pt';

    const myChart = echartsInstance.init(barGraphDivision, null, { width: 600, height: 490 });

    const option = {
        color: ['black'],
        grid: {
            bottom: 60,
            right: 10,
        },
        xAxis: [
            {
                type: 'category',
                data: notBorderedLables.data,
                position: 'bottom',
                axisTick: { show: false },
            },
            {
                type: 'category',
                data: borderedLables.data,
                position: 'bottom',
                axisTick: { show: false },
            },
            {
                type: 'category',
                data: valueLabels.data,
                position: 'bottom',
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: { formatter: () => '' },
            },
        ],
        yAxis: [
            {
                type: 'value',
                max: graphYAxisMax,
                min: graphYAxisMin,
                splitNumber: divisions,
                axisLine: { lineStyle: { color: 'black' } },
                splitLine: { lineStyle: { color: 'black' } },
                axisLabel: {
                    formatter: (value: number) => {
                        if (value == 0) return '';
                        if (isPercentage) return `${value.toFixed()}%`;
                        return value.toFixed(yAxisDecimalPlaces);
                    },
                },
            },
            {
                type: 'value',
                max: graphYAxisMax,
                min: graphYAxisMin,
                splitNumber: divisions,
                axisLine: { lineStyle: { color: 'black' } },
                splitLine: { lineStyle: { color: 'black' } },
                position: 'left',
                axisLabel: {
                    customValues: [0],
                    formatter: () => (isPercentage ? '0%' : '0'),
                },
            },
        ],
        series: [
            {
                data: graphValuesArray,
                type: 'bar',
            },
        ],
    };

    myChart.setOption(option);

    await new Promise(resolve => setTimeout(resolve, 500));

    const dataUrl = myChart.getDataURL({
        type: 'png',
        pixelRatio: 1,
        backgroundColor: '#fff',
    });

    const legendTable = buildLegendTable(graphChannels, changedIndexes);

    return {
        body: [
            [
                [
                    {
                        columns: [
                            { width: '*', text: '' },
                            {
                                width: 'auto',
                                table: {
                                    body: [
                                        [
                                            {
                                                text: graph.title,
                                                border: titleBorder,
                                                borderColor: ['red', 'red', 'red', 'red'],
                                                fontSize: 15,
                                                alignment: 'center',
                                                noWrap: true,
                                            },
                                        ],
                                    ],
                                },
                            },
                            { width: '*', text: '' },
                        ],
                        margin: [0, 10, 0, 0],
                    },
                    {
                        columnGap: 8,
                        columns: [
                            {
                                width: 'auto',
                                table: {
                                    body: [
                                        [
                                            {
                                                image: dataUrl,
                                                width: 570,
                                                height: 440,
                                                border: [false, false],
                                            },
                                        ],
                                    ],
                                    alignment: 'center',
                                },
                                layout: {
                                    paddingLeft: () => 0,
                                    paddingRight: () => 0,
                                    paddingTop: () => 0,
                                    paddingBottom: () => 0,
                                },
                            },
                            {
                                width: 'auto',
                                table: legendTable,
                                layout: {
                                    defaultBorder: false,
                                    paddingLeft: () => 4,
                                    paddingRight: () => 4,
                                    paddingTop: () => 2,
                                    paddingBottom: () => 2,
                                },
                            },
                        ],
                    },
                ],
            ],
        ],
        widths: [763],
        heights: [490],
    };
}

function prepareXAxislabel(channels: BarGraphChannel[], oldChannels: BarGraphChannel[]) {
    let newChannelsIds = new Set();
    let updatedTitles = new Set();
    let deletedChannelsLength = 0;

    let borderedLables = { data: [] as string[], customValues: [] as number[] };
    let notBorderedLables = { data: [] as string[], customValues: [] as number[] };
    const changedIndexes: Set<number> = new Set();

    if (oldChannels) {
        const oldChannelsArray = oldChannels.map(channel => channel.channel);
        const oldChannelsSet = new Set(oldChannelsArray);
        const oldChannelsTitles = oldChannels.map(channel => channel.barLabel);
        const oldChannelsLabels = new Set(oldChannelsTitles);

        channels.forEach(channel => {
            const fcuChannelId = channel?.channel?.fcuData?.fcuChannelId;
            if (fcuChannelId && !oldChannelsSet.has(channel.channel)) newChannelsIds.add(fcuChannelId);
        });

        channels.forEach(channel => {
            const label = channel?.barLabel;
            if (label && !oldChannelsLabels.has(label)) updatedTitles.add(label);
        });

        deletedChannelsLength = Math.max(oldChannels.length - channels.length, 0);
    }

    channels.forEach((channel: BarGraphChannel, index) => {
        const fcuChannelId = channel?.channel?.fcuData?.fcuChannelId;
        const position = String(index + 1);
        if (newChannelsIds.has(fcuChannelId) || updatedTitles.has(fcuChannelId)) {
            borderedLables.data.push(position);
            borderedLables.customValues.push(index);
            notBorderedLables.data.push('');
            changedIndexes.add(index);
        } else {
            notBorderedLables.data.push(position);
            notBorderedLables.customValues.push(index);
            borderedLables.data.push('');
        }
    });

    for (let i = 0; i < deletedChannelsLength; i++) {
        notBorderedLables.data.push('');
        borderedLables.data.push(String(channels.length + i + 1));
        borderedLables.customValues.push(channels.length + i);
    }

    return { borderedLables, notBorderedLables, changedIndexes };
}

function buildLegendTable(channels: BarGraphChannel[], changedIndexes: Set<number>) {
    const border = [true, true, true, true] as [boolean, boolean, boolean, boolean];
    const firstValueChanged = changedIndexes.has(0);
    const borderColor = ['black', 'black', 'black', firstValueChanged ? 'red' : 'black'] as [
        string,
        string,
        string,
        string,
    ];
    const header = [
        { text: '#', fontWeight: 'bold', border, borderColor },
        { text: 'Ch. Tag.', fontWeight: 'bold', border, borderColor },
        { text: 'Label', fontWeight: 'bold', border, borderColor },
    ];

    const rows = channels.map((channel, index) => {
        const isChanged = changedIndexes.has(index);
        const topRowChanged = changedIndexes.has(index - 1);
        const bottomRowChanged = changedIndexes.has(index + 1);

        const topRowColor = isChanged || topRowChanged ? 'red' : 'black';
        const bottomRowColor = isChanged || bottomRowChanged ? 'red' : 'black';
        const rowBorderColor = [
            isChanged ? 'red' : 'black',
            topRowColor,
            isChanged ? 'red' : 'black',
            bottomRowColor,
        ] as [string, string, string, string];

        return [
            { text: String(index + 1), border, borderColor: rowBorderColor },
            { text: channel?.channel?.fcuData?.fcuChannelId ?? '', border, borderColor: rowBorderColor },
            { text: channel?.barLabel ?? '', border, borderColor: rowBorderColor },
        ];
    });

    return {
        widths: [10, 50, 85],
        body: [header, ...rows],
    };
}

function getDecimalPlaces(channel: BarGraphChannel): number {
    const scale = channel?.channel?.fcuData?.scale || 1;
    return Math.max(Math.round(Math.log10(scale)), 0);
}

function findMaxLimit(graphChannels: BarGraphChannel[]): string {
    const maxValues = graphChannels.map((channel: BarGraphChannel) => {
        const scale = channel?.channel?.fcuData?.scale || 1;
        const maxScaled = (channel?.channel?.fcuData?.measurableRange?.max || 100) / scale;
        return maxScaled;
    });
    // Sayısal sıralama (Sayıları doğru sıralamak için Number kullanıldı)
    const sortedNumbers = maxValues.sort((a, b) => b - a);
    return String(sortedNumbers[0] ?? 100);
}

function findMinLimit(graphChannels: BarGraphChannel[]): string {
    const minValues = graphChannels.map((channel: BarGraphChannel) => {
        const scale = channel?.channel?.fcuData?.scale || 1;
        const minScaled = (channel?.channel?.fcuData?.measurableRange?.min || 0) / scale;
        return minScaled;
    });
    // Sayısal sıralama
    const sortedNumbers = minValues.sort((a, b) => a - b);
    return String(sortedNumbers[0] ?? 0);
}

function findMaxDecimalPlaces(graphChannels: BarGraphChannel[]): number {
    return graphChannels.reduce((max, channel) => Math.max(max, getDecimalPlaces(channel)), 0);
}

function generateChannelValues(graphChannels: BarGraphChannel[]) {
    return graphChannels.map((channel: BarGraphChannel) => {
        const min = channel?.channel?.fcuData?.measurableRange?.min ?? 0;
        const max = channel?.channel?.fcuData?.measurableRange?.max ?? 100;
        const scale = channel?.channel?.fcuData?.scale || 1;
        const decimalPlaces = getDecimalPlaces(channel);

        const randomRaw = Math.floor(Math.random() * (max - min + 1)) + min;
        const scaledValue = Number((randomRaw / scale).toFixed(decimalPlaces));

        return { channel, value: isNaN(scaledValue) ? 0 : scaledValue };
    });
}

function prepareValueLabels(channelValues: { channel: BarGraphChannel; value: number }[]) {
    return {
        data: channelValues.map(({ channel, value }) => value.toFixed(getDecimalPlaces(channel))),
    };
}

function prepareGraphValuesArray(
    channelValues: { channel: BarGraphChannel; value: number }[],
    maxLimit: string,
    minLimit: string,
    isPercentage: boolean
) {
    const min = Number(minLimit) || 0;
    const max = Number(maxLimit) || 100;
    const range = max - min || 1;
    return channelValues.map(({ value }) => {
        const safeVal = isNaN(value) ? 0 : value;
        return isPercentage ? ((safeVal - min) / range) * 100 : safeVal;
    });
}
